import { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CircularProgress, FormControl, FormLabel, Input, Stack, Textarea, Typography } from '@mui/joy';
import { useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import type { PublicReviewCollection } from '../../types/reviews';

export const ReviewFunnelPage = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [funnel, setFunnel] = useState<PublicReviewCollection | null>(null);
  const [loadError, setLoadError] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState('');
  useEffect(() => {
    let cancelled = false;
    setFunnel(null); setLoadError(''); setSuccess(''); setShowFeedback(false);
    api.get<PublicReviewCollection>(`/api/marketing/reviews/public/funnel/${orgSlug}/`)
      .then(response => { if (!cancelled) setFunnel(response.data); })
      .catch(() => { if (!cancelled) setLoadError('This review page is unavailable. Please contact the business directly.'); });
    return () => { cancelled = true; };
  }, [orgSlug]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting || !feedback.trim()) return;
    setSubmitting(true); setSubmitError('');
    try {
      const response = await api.post<{ thank_you_message: string }>('/api/marketing/reviews/public/feedback/', { organization_slug: orgSlug, rating, feedback_text: feedback, name, email });
      setSuccess(response.data.thank_you_message || 'Thank you for your feedback.');
      setShowFeedback(false); setFeedback('');
    } catch {
      setSubmitError('Your feedback could not be saved. Your message is still here; please try again.');
    } finally { setSubmitting(false); }
  };
  if (loadError) return <Box sx={{ p: 3 }}><Alert color="danger">{loadError}</Alert></Box>;
  if (!funnel) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress aria-label="Loading review page" /></Box>;
  const accent = /^#[\da-f]{6}$/i.test(funnel.primary_color) ? funnel.primary_color : '#2563eb';
  const platforms = funnel.available_platforms.filter(platform => /^https?:\/\//i.test(platform.review_url));
  const image = (url?: string) => url && /^https?:\/\//i.test(url) ? url : undefined;
  return <Box component="main" sx={{ minHeight: '100vh', p: { xs: 2, sm: 4 }, bgcolor: 'background.level1' }}>
    <Box sx={{ maxWidth: 840, mx: 'auto' }}>
      <Stack alignItems="center" gap={1} sx={{ mb: 3 }}>
        {image(funnel.logo_url) && <Box component="img" src={image(funnel.logo_url)} alt={`${funnel.organization_name} logo`} sx={{ maxWidth: 220, maxHeight: 90, objectFit: 'contain' }} />}
        <Typography level="h2" sx={{ textAlign: 'center' }}>{funnel.organization_name}</Typography>
      </Stack>
      <Card sx={{ p: { xs: 2, sm: 4 }, borderTop: `5px solid ${accent}`, gap: 3 }}>
        {image(funnel.project_image_url) && <Box component="figure" sx={{ m: 0 }}>
          <Box component="img" src={image(funnel.project_image_url)} alt={funnel.project_image_caption || `${funnel.organization_name} project`} sx={{ width: '100%', aspectRatio: '16 / 7', objectFit: 'cover', borderRadius: 'md' }} />
          {funnel.project_image_caption && <Typography component="figcaption" level="body-xs">{funnel.project_image_caption}</Typography>}
        </Box>}
        <Box><Typography level="h1" sx={{ fontSize: 'clamp(1.8rem, 5vw, 2.8rem)', mb: 1 }}>{funnel.collection_title || 'Share your experience'}</Typography><Typography level="body-lg">{funnel.happy_message}</Typography></Box>
        {success && <Alert color="success" role="status"><Box><Typography level="title-md">Private feedback received</Typography><Typography>{success}</Typography></Box></Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
          <Stack gap={1.5}>
            <Typography level="h3">Leave a public review</Typography>
            <Typography level="body-sm">Share your honest experience with other customers.</Typography>
            {platforms.length ? platforms.map(platform => <Button key={platform.id} component="a" href={platform.review_url} target="_blank" rel="noopener noreferrer" variant="outlined">Review on {platform.platform}</Button>) : <Alert color="neutral">Public review links are currently unavailable.</Alert>}
            <Typography level="body-xs">Opens the review platform. You complete and publish your review there.</Typography>
          </Stack>
          <Stack gap={1.5}>
            <Typography level="h3">Share private feedback</Typography>
            <Typography level="body-sm">Send comments or questions directly to our team.</Typography>
            <Button variant="outlined" onClick={() => setShowFeedback(!showFeedback)} aria-expanded={showFeedback} aria-controls="private-feedback" disabled={!!success}>Write private feedback</Button>
          </Stack>
        </Box>
        {showFeedback && <Stack component="form" id="private-feedback" onSubmit={submit} gap={2}>
          <Typography level="h3">{funnel.unhappy_title || 'Your feedback'}</Typography>
          <Typography>{funnel.unhappy_message}</Typography>
          <Box><Typography level="title-sm" id="rating-label">Optional rating</Typography><Stack direction="row" gap={1} flexWrap="wrap" aria-labelledby="rating-label">
            {[1, 2, 3, 4, 5].map(value => <Button key={value} type="button" aria-label={`${value} star${value === 1 ? '' : 's'}`} aria-pressed={rating === value} variant={rating === value ? 'solid' : 'outlined'} onClick={() => setRating(rating === value ? null : value)}>{value} ★</Button>)}
          </Stack></Box>
          <FormControl required><FormLabel>Your feedback</FormLabel><Textarea required value={feedback} onChange={e => setFeedback(e.target.value)} minRows={4} slotProps={{ textarea: { maxLength: 10000 } }} /></FormControl>
          <FormControl><FormLabel>Name (optional)</FormLabel><Input value={name} onChange={e => setName(e.target.value)} slotProps={{ input: { maxLength: 255 } }} /></FormControl>
          <FormControl><FormLabel>Email (optional, for a reply)</FormLabel><Input type="email" value={email} onChange={e => setEmail(e.target.value)} /></FormControl>
          {submitError && <Alert color="danger">{submitError}</Alert>}
          <Button type="submit" loading={submitting} disabled={!feedback.trim()}>Send private feedback</Button>
          <Typography level="body-xs">Private feedback is shared with the business and is not posted publicly.</Typography>
        </Stack>}
      </Card>
      <Typography level="body-xs" sx={{ textAlign: 'center', mt: 3 }}>Powered by ApexOS</Typography>
    </Box>
  </Box>;
};
